import {
  ID_DOC_TIPO_IDS,
  INE_COMPLETO_TIPO_ID,
  INE_FRENTE_TIPO_ID,
  INE_REVERSO_TIPO_ID,
  PASAPORTE_TIPO_ID,
  resolverIdentidad,
  type LatestDoc,
  type OrigenIdentidad,
} from '@/utils/expediente-obligatorios';

/**
 * Identificación oficial VIGENTE de una persona a partir de sus filas de
 * `documentos`, para los portales que pintan el expediente propio (agente,
 * inmobiliaria, embajador).
 *
 * Antes cada portal decidía por su cuenta y casi todos exigían el par Frente +
 * Reverso (2 + 3): un INE completo (63) subido se pintaba "Pendiente" aunque
 * existiera. La regla es la de `resolverIdentidad` (la misma del admin y la app):
 * INE completo (63) o pasaporte (4); el par 2/3 solo cuenta como lectura legacy
 * mientras siga validado.
 *
 * Una diferencia a propósito: la function `expediente-documento` expira el grupo
 * al subir una identificación nueva y deja la fila más vieja ACTIVA con estatus 4
 * (auditoría). Si se le pasara tal cual a `resolverIdentidad`, un 63 expirado
 * "mandaría" sobre el pasaporte recién subido. Por eso, cuando hay alguna
 * identificación no expirada, las expiradas se ignoran.
 */

export type EstadoIdentidad = 'pendiente' | 'validado' | 'revision' | 'rechazado' | 'expirado';

export interface FilaDocumentoIdentidad {
  id: number;
  id_tipo_documento: number;
  id_estatus_verificacion: number | null;
  url?: string | null;
  fecha_creacion?: string | null;
}

export interface IdentidadVigente<T extends FilaDocumentoIdentidad> {
  estado: EstadoIdentidad;
  /** Cumple el requisito (validada). */
  cumplida: boolean;
  origen: OrigenIdentidad;
  /** Tipo que respalda la identificación hoy: 63, 4, 2 (par legacy) o null. */
  tipo: number | null;
  /** Filas que la respaldan: [63], [4] o [frente, reverso] del legacy. */
  filas: T[];
  /** El legacy existe pero ya no vale: el único camino es subir INE completo o pasaporte. */
  exigeIneCompleto: boolean;
}

const ESTATUS_EXPIRADO = 4;

function estadoDeEstatus(ev: number | null | undefined): EstadoIdentidad {
  if (ev === 2) return 'validado';
  if (ev === 3) return 'rechazado';
  if (ev === 4) return 'expirado';
  return 'revision';
}

/** Más reciente primero; `fecha_creacion` nula cuenta como la más nueva (igual que el resolvedor). */
function masReciente<T extends FilaDocumentoIdentidad>(a: T, b: T): T {
  const fa = a.fecha_creacion ?? '9999-12-31T23:59:59Z';
  const fb = b.fecha_creacion ?? '9999-12-31T23:59:59Z';
  if (fa !== fb) return fa > fb ? a : b;
  return a.id >= b.id ? a : b;
}

export function identidadVigente<T extends FilaDocumentoIdentidad>(filas: readonly T[]): IdentidadVigente<T> {
  const deIdentidad = filas.filter((f) => ID_DOC_TIPO_IDS.includes(f.id_tipo_documento));
  const noExpiradas = deIdentidad.filter((f) => f.id_estatus_verificacion !== ESTATUS_EXPIRADO);
  const universo = noExpiradas.length > 0 ? noExpiradas : deIdentidad;

  const ultima = new Map<number, T>();
  for (const f of universo) {
    const previa = ultima.get(f.id_tipo_documento);
    ultima.set(f.id_tipo_documento, previa ? masReciente(previa, f) : f);
  }

  // `resolverIdentidad` trabaja sobre el mapa (persona, tipo) → último doc. Aquí hay
  // una sola persona: se usa una clave fija.
  const PERSONA = 1;
  const latest: Record<string, LatestDoc> = {};
  for (const [tipo, f] of ultima) {
    latest[`${PERSONA}__${tipo}`] = {
      id: f.id,
      estatusId: f.id_estatus_verificacion ?? 0,
      fecha: f.fecha_creacion ?? '9999-12-31T23:59:59Z',
    };
  }
  const r = resolverIdentidad(PERSONA, latest);
  const fila = (t: number) => ultima.get(t);
  const parLegacy = () => [fila(INE_FRENTE_TIPO_ID), fila(INE_REVERSO_TIPO_ID)].filter(Boolean) as T[];

  const base = { cumplida: r.cumplida, origen: r.origen, exigeIneCompleto: r.exigeIneCompleto };

  if (r.cumplida) {
    if (r.origen === 'ine_legacy') {
      return { ...base, estado: 'validado', tipo: INE_FRENTE_TIPO_ID, filas: parLegacy() };
    }
    const tipo = r.tiposVigentes[0] ?? null;
    return { ...base, estado: 'validado', tipo, filas: tipo != null && fila(tipo) ? [fila(tipo)!] : [] };
  }

  // INE completo subido pero aún no validado (en revisión, rechazado o expirado).
  const ine = fila(INE_COMPLETO_TIPO_ID);
  if (ine) {
    return { ...base, estado: estadoDeEstatus(ine.id_estatus_verificacion), tipo: INE_COMPLETO_TIPO_ID, filas: [ine] };
  }
  const pasaporte = fila(PASAPORTE_TIPO_ID);
  if (pasaporte) {
    return { ...base, estado: estadoDeEstatus(pasaporte.id_estatus_verificacion), tipo: PASAPORTE_TIPO_ID, filas: [pasaporte] };
  }
  // Legacy sin validar: se muestra su estado (para que se vea y se pueda consultar),
  // pero ya no se completa con otro frente/reverso: se reemplaza con 63 o 4.
  const frente = fila(INE_FRENTE_TIPO_ID);
  if (frente) {
    return { ...base, estado: estadoDeEstatus(frente.id_estatus_verificacion), tipo: INE_FRENTE_TIPO_ID, filas: parLegacy() };
  }
  // Solo un reverso (o nada): no hay identificación.
  return { ...base, estado: 'pendiente', tipo: null, filas: parLegacy() };
}
